import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  MapPin, ShieldCheck, GraduationCap, Users, Calendar, Award, Check, ArrowRight,
  Building, Home as HomeIcon, BookOpen, ChevronRight, Phone, MessageCircle,
  FileText, ClipboardCheck, Thermometer, Plane, Star, FileDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import FaqSection from '../components/FaqSection';
import { universities as fallbackUnis, fmtRub, inrApprox } from '../data/mock';
import { useUniversities } from '../hooks/useUniversities';
import { buildUniversityFaqs } from '../data/faqs';
import SEO from '../components/SEO';
import { publicUniversityLogos } from '../lib/uniLogosApi';
import { submitInquiry } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const BASE = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (src && src.startsWith('/') ? `${BASE}${src}` : src);

export default function UniversityDetail() {
  const { slug } = useParams();
  const list = useUniversities(fallbackUnis);
  const universities = list.length ? list : fallbackUnis;
  const uni = universities.find((u) => u.slug === slug);
  const { toast } = useToast();
  const [cbForm, setCbForm] = useState({ name: '', phone: '', email: '', neet: '' });
  const [cbSending, setCbSending] = useState(false);

  const submitCallback = async (e) => {
    e.preventDefault();
    if (!cbForm.name.trim() || !cbForm.phone.trim()) {
      toast({ variant: 'destructive', title: 'Missing info', description: 'Please enter your name and phone number.' });
      return;
    }
    setCbSending(true);
    try {
      await submitInquiry({
        type: 'contact',
        name: cbForm.name.trim(),
        phone: cbForm.phone.trim(),
        email: cbForm.email.trim() || undefined,
        neet: cbForm.neet || undefined,
        variant: 'university-callback',
        source: `/universities/${slug}`,
        message: `Request Callback · ${uni ? uni.name : slug}`,
      });
      toast({ title: 'Callback requested!', description: 'Our counsellor will call you within 24 hours.' });
      setCbForm({ name: '', phone: '', email: '', neet: '' });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Something went wrong', description: err?.message || 'Please try again.' });
    } finally {
      setCbSending(false);
    }
  };
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    let off = false;
    publicUniversityLogos()
      .then((m) => { if (!off) setLogo(m[slug] ? `${BASE}${m[slug]}` : null); })
      .catch(() => {});
    return () => { off = true; };
  }, [slug]);
  if (!uni) return <Navigate to="/universities" replace />;

  const related = universities.filter((u) => u.slug !== uni.slug).slice(0, 4);

  return (
    <div className="bg-background">
      <SEO
        title={`${uni.name} — MBBS Fees, Ranking & Admission | Russian Education`}
        description={`${uni.name} in ${uni.city}, Russia — NMC-approved, WHO-recognised medical university. English medium, ${uni.fee}/yr, hostel & Indian food available. Direct admission with Russian Education.`}
        path={`/universities/${uni.slug}`}
        image={resolveImg(uni.building)}
      />
      {/* Hero + Breadcrumbs */}
      <section className="relative bg-primary text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={resolveImg(uni.building)} alt={uni.name} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/70" />
        </div>

        <div className="relative container mx-auto max-w-7xl px-4 md:px-8 pt-6 md:pt-8 pb-14 md:pb-20">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-white/70">
            <Link to="/" className="hover:text-brand transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/universities" className="hover:text-brand transition-colors">Universities</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white font-semibold truncate max-w-[240px] md:max-w-full">{uni.name}</span>
          </nav>

          <div className="mt-6 md:mt-10 grid md:grid-cols-[1fr_320px] gap-8 items-start">
            <div className="animate-fade-up">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-brand/40 bg-brand/10 text-white text-xs font-semibold">
                  <ShieldCheck className="w-3.5 h-3.5 text-brand" /> NMC (India) Approved
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/25 bg-white/10 text-white text-xs font-semibold">
                  <ShieldCheck className="w-3.5 h-3.5" /> WHO Recognized
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/25 bg-white/10 text-white text-xs font-semibold">
                  <ShieldCheck className="w-3.5 h-3.5" /> FAIMER
                </span>
              </div>

              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mt-4 leading-[1.05] flex items-center gap-4 flex-wrap">
                {logo && (
                  <img
                    src={logo}
                    alt={`${uni.name} logo`}
                    data-testid="uni-logo"
                    className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-white p-1.5 shadow-lg shrink-0 object-contain"
                  />
                )}
                <span>{uni.name}</span>
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-white/85 text-sm md:text-base">
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-brand" /> {uni.city}, {uni.region}</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-brand" /> Est. {uni.established}</span>
                <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-brand" /> {uni.ranking}</span>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a href="https://wa.me/919509406802" target="_blank" rel="noreferrer">
                  <Button className="bg-brand hover:bg-brand-dark text-white rounded-full h-11 px-6 btn-shift font-semibold">
                    Apply for Admission <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </a>
                <a href="tel:+919509406802">
                  <Button variant="outline" className="h-11 px-6 rounded-full bg-transparent text-white border-white/40 hover:bg-white hover:text-primary font-semibold">
                    <Phone className="w-4 h-4 mr-2" /> Free Counselling
                  </Button>
                </a>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/universities/${uni.slug}/schedule.pdf`}
                  target="_blank" rel="noreferrer"
                  data-testid="uni-download-pdf"
                >
                  <Button variant="outline" className="h-11 px-6 rounded-full bg-white/95 text-primary border-white hover:bg-brand hover:text-white hover:border-brand font-semibold">
                    <FileDown className="w-4 h-4 mr-2" /> Schedule of Fees (PDF)
                  </Button>
                </a>
              </div>
            </div>

            <div className="w-full bg-white/10 backdrop-blur rounded-2xl p-5 border border-white/15">
              <div className="text-[11px] uppercase tracking-widest text-brand font-bold">Annual Tuition</div>
              <div className="font-serif text-4xl md:text-5xl font-bold mt-1 text-white">{uni.fee}</div>
              <div className="text-xs text-white/70 mt-1">6-Year Total Est. {uni.inrEstimate}</div>
              <div className="mt-4 pt-4 border-t border-white/15 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Duration</div>
                  <div className="font-bold">{uni.duration}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Indians</div>
                  <div className="font-bold">{uni.indianStudents}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Medium</div>
                  <div className="font-bold">{uni.medium}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Type</div>
                  <div className="font-bold text-xs">{uni.type}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats bar */}
      <section className="py-8 border-b border-border bg-white">
        <div className="container mx-auto max-w-7xl px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Building, label: 'Established', value: uni.established },
            { icon: Users, label: 'Total Students', value: uni.students },
            { icon: GraduationCap, label: 'Indian Students', value: uni.indianStudents },
            { icon: Award, label: 'Ranking', value: uni.ranking },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{s.label}</div>
                <div className="font-serif font-bold text-primary text-lg">{s.value}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Main body */}
      <section className="py-14 md:py-20">
        <div className="container mx-auto max-w-7xl px-4 md:px-8 grid lg:grid-cols-[1fr_360px] gap-10">
          <div className="space-y-14">
            {/* About */}
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-brand font-bold">Overview</div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mt-2">
                About <span className="text-brand">{uni.short}</span>
              </h2>
              <p className="mt-5 text-muted-foreground leading-relaxed text-[15px]">{uni.about}</p>
            </div>

            {/* Why choose */}
            <div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary">Why Choose <span className="text-brand">{uni.short}</span>?</h3>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {uni.whyChoose.map((w, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-border shadow-sm">
                    <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center shrink-0 font-bold text-xs">{i + 1}</div>
                    <span className="text-sm text-foreground font-medium leading-snug">{w}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Highlights */}
            <div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary">Key Highlights</h3>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {uni.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-2.5 p-3.5 rounded-xl bg-muted border border-border">
                    <div className="w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    </div>
                    <span className="text-sm text-foreground font-medium leading-snug">{h}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Fee Structure Year-wise */}
            <div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary">Year-wise Fee Structure</h3>
              <p className="mt-1 text-sm text-muted-foreground">Fees in Russian Ruble · 1 ₽ ≈ ₹1.23 INR (rate at payment time may vary)</p>
              <div className="mt-4 overflow-x-auto rounded-2xl border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Year</th>
                      <th className="text-right px-4 py-3 font-semibold">Tuition</th>
                      <th className="text-right px-4 py-3 font-semibold">Hostel</th>
                      <th className="text-right px-4 py-3 font-semibold">Food (mo.)</th>
                      <th className="text-right px-4 py-3 font-semibold">Medical & Visa</th>
                      <th className="text-right px-4 py-3 font-semibold">Total ₽</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(uni.feeStructure || []).map((f, i) => {
                      const tuition = f.tuition_rub ?? f.tuition ?? 0;
                      const hostel = f.hostel_rub ?? f.hostel ?? 0;
                      const foodM  = f.food_usd_per_month ?? 100;
                      const foodY  = (f.food_usd_per_month ? f.food_usd_per_month * 12 * 90 : (f.food || 0));
                      const medVi  = f.medical_visa_rub ?? f.misc ?? 6000;
                      const tot = tuition + hostel + foodY + medVi;
                      return (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-muted/40'}>
                          <td className="px-4 py-3 font-medium text-primary">{f.label}</td>
                          <td className="px-4 py-3 text-right">₽{tuition.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-right">₽{hostel.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-right">${foodM}/mo</td>
                          <td className="px-4 py-3 text-right">₽{medVi.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-right font-bold text-brand">₽{tot.toLocaleString('en-IN')}</td>
                        </tr>
                      );
                    })}
                    <tr className="bg-brand/10 border-t-2 border-brand">
                      <td className="px-4 py-3 font-bold text-primary">6-Year Total</td>
                      <td colSpan={4} className="px-4 py-3 text-right text-xs text-muted-foreground">
                        {uni.totalFeeRUB ? <>Tuition only: ₽{uni.totalFeeRUB.toLocaleString('en-IN')} · </> : null}Approx INR
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-brand text-base">{uni.inrEstimate}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Eligibility */}
            <div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary">Eligibility Criteria</h3>
              <ul className="mt-4 space-y-2.5">
                {uni.eligibility.map((e, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-foreground">
                    <BookOpen className="w-4 h-4 mt-1 text-brand shrink-0" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Documents */}
            <div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary">Documents Required</h3>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {uni.documents.map((d, i) => (
                  <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-white border border-border">
                    <FileText className="w-4 h-4 mt-0.5 text-brand shrink-0" />
                    <span className="text-sm text-foreground">{d}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Facilities */}
            <div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary">Campus Facilities</h3>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {uni.facilities.map((f, i) => (
                  <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-muted border border-border">
                    <Star className="w-4 h-4 mt-0.5 text-brand shrink-0" fill="#FC2120" strokeWidth={0} />
                    <span className="text-sm text-foreground">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Admission Process */}
            <div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary">Admission Process</h3>
              <ol className="mt-5 space-y-3">
                {uni.admissionProcess.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-border shadow-sm">
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center shrink-0 font-bold text-sm">{i + 1}</div>
                    <span className="text-sm text-foreground font-medium mt-1">{p}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Hostel + Climate */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-5 rounded-2xl bg-brand/5 border border-brand/15">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center"><HomeIcon className="w-5 h-5" /></div>
                  <h4 className="font-serif text-xl font-bold text-primary">Hostel</h4>
                </div>
                <p className="text-sm text-foreground leading-relaxed">{uni.hostelInfo}</p>
              </div>
              <div className="p-5 rounded-2xl bg-primary/5 border border-primary/15">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center"><Thermometer className="w-5 h-5" /></div>
                  <h4 className="font-serif text-xl font-bold text-primary">Climate</h4>
                </div>
                <p className="text-sm text-foreground leading-relaxed">{uni.climateInfo}</p>
              </div>
            </div>

            {/* Airport */}
            <div className="p-5 rounded-2xl bg-white border border-border shadow-sm flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-brand/10 text-brand flex items-center justify-center shrink-0"><Plane className="w-5 h-5" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Nearest Airport</div>
                <div className="text-sm font-semibold text-primary">{uni.nearestAirport}</div>
              </div>
            </div>
          </div>

          {/* Sidebar - Enquiry form */}
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="bg-white rounded-2xl border border-border shadow-lg p-6">
              <div className="text-xs uppercase tracking-widest text-brand font-bold">Free Enquiry</div>
              <h4 className="font-serif text-xl font-bold text-primary mt-1">Get Admission Guidance</h4>
              <p className="text-xs text-muted-foreground mt-1">Talk to a verified counsellor in 24 hrs.</p>

              <form onSubmit={submitCallback} className="mt-4 space-y-3">
                <input value={cbForm.name} onChange={(e) => setCbForm((s) => ({ ...s, name: e.target.value }))} placeholder="Full Name" required data-testid="uni-cb-name" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                <input value={cbForm.phone} onChange={(e) => setCbForm((s) => ({ ...s, phone: e.target.value }))} placeholder="Phone Number" required inputMode="tel" data-testid="uni-cb-phone" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                <input value={cbForm.email} onChange={(e) => setCbForm((s) => ({ ...s, email: e.target.value }))} placeholder="Email" type="email" data-testid="uni-cb-email" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                <input value={cbForm.neet} onChange={(e) => setCbForm((s) => ({ ...s, neet: e.target.value }))} placeholder="NEET Score" data-testid="uni-cb-neet" className="w-full h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                <Button type="submit" disabled={cbSending} data-testid="uni-cb-submit" className="w-full bg-brand hover:bg-brand-dark text-white h-11 rounded-lg font-semibold btn-shift disabled:opacity-60">
                  {cbSending ? 'Sending…' : 'Request Callback'}
                </Button>
              </form>

              <div className="mt-5 pt-5 border-t border-border space-y-3">
                <a href="tel:+919509406802" className="flex items-center gap-3 group">
                  <div className="w-10 h-10 rounded-full bg-brand/10 text-brand flex items-center justify-center group-hover:bg-brand group-hover:text-white transition-colors">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Call Now</div>
                    <div className="text-sm font-bold text-primary group-hover:text-brand">+91 95094 06802</div>
                  </div>
                </a>
                <a href="https://wa.me/919509406802" target="_blank" rel="noreferrer" className="flex items-center gap-3 group">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                    <MessageCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">WhatsApp</div>
                    <div className="text-sm font-bold text-primary group-hover:text-emerald-600">Chat 24/7</div>
                  </div>
                </a>
              </div>
            </div>

            <Link to="/universities" className="mt-4 flex items-center justify-center gap-2 text-sm font-bold text-brand hover:text-brand-dark">
              <ClipboardCheck className="w-4 h-4" /> Compare with other universities
            </Link>
          </aside>
        </div>
      </section>

      {/* FAQ section */}
      <FaqSection
        eyebrow={`${uni.short || uni.name} FAQ`}
        title="Frequently Asked Questions"
        subtitle={`Everything Indian aspirants ask about ${uni.name} — from study medium to hostels, food and clinical rotations.`}
        faqs={buildUniversityFaqs(uni)}
        variant="blue"
        testid="uni-faq"
      />

      {/* Related universities */}
      <section className="py-14 md:py-20 bg-muted/60">
        <div className="container mx-auto max-w-7xl px-4 md:px-8">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-brand font-bold">Explore More</div>
              <h3 className="font-serif text-3xl md:text-4xl font-bold text-primary mt-2">Other Top Universities</h3>
            </div>
            <Link to="/universities" className="hidden md:inline-flex items-center gap-1 text-brand text-sm font-bold group">
              View All 66 Universities <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((u, i) => (
              <Link to={`/universities/${u.slug}`} key={i} className="group bg-white rounded-2xl overflow-hidden border border-border card-lift">
                <div className="relative aspect-[4/3]">
                  <img src={resolveImg(u.building)} alt={u.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/70 to-transparent" />
                  <div className="absolute bottom-2 left-3 text-white text-xs font-semibold">
                    <MapPin className="w-3 h-3 inline mr-1" />{u.city}
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="font-serif text-sm font-bold text-primary leading-tight line-clamp-2 group-hover:text-brand transition-colors">{u.name}</h4>
                  <div className="mt-2 text-xs font-bold text-brand">{u.fee} / yr</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
